import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { TableColumnType } from "src/app/constants/table";
import { BenefitAddWizardPanel, BenefitBuyupWizardPanel, BenefitWizardType } from "src/app/constants/wizard-panels";
import { BenefitSelection, BenefitSelectionItem, BenefitSelectionPayload } from "src/app/models/benefit";
import { BuyupKitBenefitSelection, KitBenefit, KitBenefitBuyUpPayload, KitBenefitBuyUpTableData, KitBenefitCollection, KitBenefitPayload } from "src/app/models/kit-benefit";
import { TableColumn } from "src/app/models/table";
import { EnrollmentManagerService } from "src/app/services/enrollment-manager.service";
import { FormValidationService } from "src/app/services/form-validation.service";
import { HelperService } from "src/app/services/helper.service";

@Component({
    selector: 'mwp-benefit-enrollment-wizard-selection',
    templateUrl: './benefit-selection.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BenefitSelectionComponent implements OnInit {
    @Input() public benefitSelection: BenefitSelection;
    @Input() public kitBenefitCollection: KitBenefitCollection;
    @Input() public kitBuyUpBenefitCollection: KitBenefitCollection;
    @Output() public emitCancel: EventEmitter<any> = new EventEmitter();
    @Output() public emitComplete: EventEmitter<any> = new EventEmitter();

    public selectAllEnabled: boolean;
    public displayBackButton: boolean;
    public isKitBenefit: boolean;
    public isKitBuyUpBenefit: boolean;
    public isFutureBenefit: boolean;
    public formSelectCheckbox: string = 'isSelected';
    public formBenefitNumber: string = 'benefitNumber';
    public formEnrollmentName: string = 'enrollmentName';
    public formCurrentCoverage: string = 'currentCoverage';
    public formAvailableOn: string = 'availableOn';
    public formUnAvailableOn: string = 'unavailableOn';
    public formMemberBenefitID: string = 'memberBenefitID';
    public formUniqueID: string = 'uniqueID';
    public formIsOneSlideToggleSelected: string = 'isOneSlideToggleSelected';
    public benefitSelectionForm: FormGroup;
    public benefitSelectionFormArrayName: string = 'benefitSelectionFormArrayName';
    public tableMatData: MatTableDataSource<any>;
    public tableColumns: TableColumn[] = [
        {
            columnName: 'Benefit',
            propertyId: this.formEnrollmentName,
            columnType: TableColumnType.label
        },
    ];

    constructor(
        private formBuilder: FormBuilder,
        private enrollmentManagerService: EnrollmentManagerService,
        private helperService: HelperService,
    ) {
    }

    ngOnInit() {
        this.tableColumnBuilder();
        this.formArrayBuilder();
    }

    private tableColumnBuilder(): void {
        let l_CheckBoxTableColumnType: TableColumnType = TableColumnType.checkBox;
        let l_IsSingleSelect: boolean;
        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Term:
                l_IsSingleSelect = !this.benefitSelection.isMultiSelect;
                break;
            
            case BenefitWizardType.BuyUp:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitBuyupWizardPanel.optionalBenefits:
                        this.isKitBuyUpBenefit = true;

                        this.tableColumns.push({
                            columnName: 'Current Coverage',
                            propertyId: this.formCurrentCoverage,
                            columnType: TableColumnType.label,
                            width: '15%',
                        });

                        let l_IsAddSelected: boolean;
                        let l_IsChangeSelected: boolean;
                        let l_IsTermSelected: boolean;

                        this.kitBuyUpBenefitCollection.items.forEach((item: KitBenefit) => { 
                            if (!l_IsTermSelected)
                                l_IsTermSelected = item.isTermAvailable;

                            if (!l_IsChangeSelected)
                                l_IsChangeSelected = item.isChangeAvailable;

                            if (!l_IsAddSelected)
                                l_IsAddSelected = !item.isChangeAvailable;
                        });

                        if (l_IsTermSelected)
                            this.tableColumns.unshift({
                                columnName: 'Term',
                                propertyId: 'isTermSelected',
                                columnType: TableColumnType.slideToggle,
                                dataTypeText: 'Term',
                                isSingleRowSelect: true,
                                hideElementByPropertyId: 'isTermAvailable',
                                width: '10%',
                            });

                        if (l_IsChangeSelected)
                            this.tableColumns.unshift({
                                columnName: 'Change',
                                propertyId: 'isChangeSelected',
                                columnType: TableColumnType.slideToggle,
                                dataTypeText: 'Change',
                                isSingleRowSelect: true,
                                hideElementByPropertyId: 'isChangeAvailable',
                                width: '10%',
                            });

                        if (l_IsAddSelected)
                            this.tableColumns.unshift({
                                columnName: 'Add',
                                propertyId: 'isAddSelected',
                                columnType: TableColumnType.slideToggle,
                                dataTypeText: 'Add',
                                isSingleRowSelect: true,
                                hideElementByPropertyId: 'isAddAvailable',
                                width: '10%',
                            });
                        break;
                }
                break;

            case BenefitWizardType.Add:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitAddWizardPanel.kitBenefits:
                        this.isKitBenefit = true;
                        l_CheckBoxTableColumnType = this.kitBenefitCollection.items.length > 1 ? TableColumnType.checkBoxSelectAll : TableColumnType.checkBox;
                        break;

                    case BenefitAddWizardPanel.futureBenefits:
                        this.isFutureBenefit = true;
                        l_CheckBoxTableColumnType = TableColumnType.checkBox;
                        l_IsSingleSelect = !this.benefitSelection.isMultiSelect;
                        this.addAvailableOnColumn();

                        if (this.benefitSelection.showUnavailableColumn)
                            this.tableColumns.push({
                                columnName: 'Unavailable On',
                                propertyId: this.formUnAvailableOn,
                                columnType: TableColumnType.label
                            });
                        break;

                    default:
                        l_IsSingleSelect = !this.benefitSelection.isMultiSelect;
                        this.addAvailableOnColumn();
                        break;
                }
                break;

            default:
                l_IsSingleSelect = !this.benefitSelection.isMultiSelect;
                this.addAvailableOnColumn();
                break;
        }

        if (!this.isKitBuyUpBenefit) {
            this.tableColumns.unshift({
                columnName: '',
                propertyId: this.formSelectCheckbox,
                columnType: l_CheckBoxTableColumnType,
                isSingleSelect: l_IsSingleSelect,
                dataTypePropertyId: this.formBenefitNumber,
                width: '5%',
            });
        }
    }

    private addAvailableOnColumn(): void {
        this.tableColumns.push({
            columnName: 'Available On',
            propertyId: this.formAvailableOn,
            columnType: TableColumnType.label
        });
    }

    private formArrayBuilder(): void {
        let l_FormArray: FormArray;
        
        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Term:
                this.tableMatData = new MatTableDataSource<BenefitSelectionItem>();
                this.tableMatData.data = this.benefitSelection.items;

                l_FormArray = new FormArray([], FormValidationService.formArrayRequiresCheckboxChecked(this.formSelectCheckbox));
                this.benefitSelection.items.forEach((item: BenefitSelectionItem) => {
                    l_FormArray.push(
                        this.formBuilder.group({
                            [this.formEnrollmentName]: [item.enrollmentName],
                            [this.formAvailableOn]: [item.availableOn],
                            [this.formBenefitNumber]: [item.benefitNumber],
                            [this.formUniqueID]: [item.uniqueID],
                            [this.formMemberBenefitID]: [item.memberBenefitID],
                            [this.formSelectCheckbox]: [false],
                        })
                    );
                });
                break;

            case BenefitWizardType.BuyUp:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitBuyupWizardPanel.optionalBenefits:
                        const l_BolleanChecks: string[] = ['isAddSelected', 'isChangeSelected', 'isTermSelected'];
                        l_FormArray = new FormArray([], FormValidationService.formArrayRequiresOneBooleanTrue(l_BolleanChecks));
                        this.tableMatData = new MatTableDataSource<KitBenefitBuyUpTableData>();
                        const l_KitBenefitBuyUpTableData: KitBenefitBuyUpTableData[] = new Array<KitBenefitBuyUpTableData>();

                        this.kitBuyUpBenefitCollection.items.forEach((item: KitBenefit) => {
                            let l_KitBenefitBuyUpTableDataItem: KitBenefitBuyUpTableData = this.helperService.convertObjectIntoObject(item, new KitBenefitBuyUpTableData());
                            l_KitBenefitBuyUpTableDataItem.isAddAvailable = !l_KitBenefitBuyUpTableDataItem.isChangeAvailable;

                            l_FormArray.push(
                                this.formBuilder.group({
                                    [this.formUniqueID]: [item.uniqueID],
                                    [this.formEnrollmentName]: [item.enrollmentName],
                                    [this.formCurrentCoverage]: [item.currentCoverage],
                                    isAddSelected: [item.isAddSelected],
                                    isChangeSelected: [item.isChangeSelected],
                                    isTermSelected: [item.isTermSelected],
                                    [this.formIsOneSlideToggleSelected]: [null]
                                })
                            );

                            l_KitBenefitBuyUpTableData.push(l_KitBenefitBuyUpTableDataItem);
                        });

                        this.tableMatData.data = l_KitBenefitBuyUpTableData;
                        break;

                    default:
                        this.tableMatData = new MatTableDataSource<BenefitSelectionItem>();
                        this.tableMatData.data = this.benefitSelection.items;

                        l_FormArray = new FormArray([], FormValidationService.formArrayRequiresCheckboxChecked(this.formSelectCheckbox));
                        this.benefitSelection.items.forEach((item: BenefitSelectionItem) => {
                            l_FormArray.push(
                                this.formBuilder.group({
                                    [this.formEnrollmentName]: [item.enrollmentName],
                                    [this.formAvailableOn]: [item.availableOn],
                                    [this.formUnAvailableOn]: [item.unavailableOn],
                                    [this.formBenefitNumber]: [item.benefitNumber],
                                    [this.formUniqueID]: [item.uniqueID],
                                    [this.formSelectCheckbox]: [false],
                                })
                            );
                        });
                        break;
                }
                break;

            default:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitAddWizardPanel.kitBenefits:
                        l_FormArray = new FormArray([]);
                        this.tableMatData = new MatTableDataSource<KitBenefit>();
                        this.tableMatData.data = this.kitBenefitCollection.items;

                        this.kitBenefitCollection.items.forEach((item: KitBenefit) => {
                            l_FormArray.push(
                                this.formBuilder.group({
                                    [this.formUniqueID]: [item.uniqueID],
                                    [this.formSelectCheckbox]: [item.isSelected, item.isRequired ? (Validators.required, { disabled: true }) : null],
                                    [this.formEnrollmentName]: [item.enrollmentName],
                                    isChangeAvailable: [item.isChangeAvailable],
                                    isTermAvailable: [item.isTermAvailable],
                                    isRequired: [item.isRequired],
                                    isChangeSelected: [item.isChangeSelected],
                                    isTermSelected: [item.isTermSelected],
                                })
                            );
                        });
                        break;

                    default:
                        this.tableMatData = new MatTableDataSource<BenefitSelectionItem>();
                        this.tableMatData.data = this.benefitSelection.items;

                        l_FormArray = new FormArray([], FormValidationService.formArrayRequiresCheckboxChecked(this.formSelectCheckbox));
                        this.benefitSelection.items.forEach((item: BenefitSelectionItem) => {
                            l_FormArray.push(
                                this.formBuilder.group({
                                    [this.formEnrollmentName]: [item.enrollmentName],
                                    [this.formAvailableOn]: [item.availableOn],
                                    [this.formUnAvailableOn]: [item.unavailableOn],
                                    [this.formBenefitNumber]: [item.benefitNumber],
                                    [this.formUniqueID]: [item.uniqueID],
                                    [this.formSelectCheckbox]: [false],
                                })
                            );
                        });
                        break;
                }
                break;
        }
        

        this.benefitSelectionForm = this.formBuilder.group({
            [this.benefitSelectionFormArrayName]: l_FormArray,
        });
    }

    public complete(): void {
        const l_AssignArray = <FormArray>this.benefitSelectionForm.get(this.benefitSelectionFormArrayName) as FormArray;
        let l_Payload: any = new BenefitSelectionPayload();

        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Term:
                l_AssignArray.controls.map((gaa) => {
                    if (gaa.get(this.formSelectCheckbox).value) {
                        l_Payload.memberBenefitIDs.push(gaa.get(this.formMemberBenefitID).value);
                        l_Payload.selectedBenefitName = gaa.get(this.formEnrollmentName).value;
                        l_Payload.selectedBenefitAvailable = gaa.get(this.formAvailableOn).value;
                    }
                });
                break;

            case BenefitWizardType.BuyUp:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitBuyupWizardPanel.optionalBenefits:
                        l_Payload = new KitBenefitBuyUpPayload();
                        l_AssignArray.controls.map((gaa) => {
                            const l_BuyupKitBenefitSelection: BuyupKitBenefitSelection = this.helperService.convertFormGroupIntoObject(gaa as FormGroup, new BuyupKitBenefitSelection());
                            l_BuyupKitBenefitSelection.isAddSelected = !!l_BuyupKitBenefitSelection.isAddSelected;
                            l_BuyupKitBenefitSelection.isChangeSelected = !!l_BuyupKitBenefitSelection.isChangeSelected;
                            l_BuyupKitBenefitSelection.isTermSelected = !!l_BuyupKitBenefitSelection.isTermSelected;
                            l_BuyupKitBenefitSelection.uniqueId = gaa.get(this.formUniqueID).value;
                            l_Payload.selectedBuyupKitBenefits.push(l_BuyupKitBenefitSelection);
                        });
                    break;
  
                    default:
                        l_AssignArray.controls.map((gaa) => {
                            if (gaa.get(this.formSelectCheckbox).value) {
                                l_Payload.uniqueIDs.push(gaa.get(this.formUniqueID).value);
                                l_Payload.selectedBenefitName = gaa.get(this.formEnrollmentName).value;
                                l_Payload.selectedBenefitAvailable = gaa.get(this.formAvailableOn).value;
                            }
                        });
                        break;
                }
                break;

            default:
                switch (this.enrollmentManagerService.benefitWizardControlPanel.panel) {
                    case BenefitAddWizardPanel.kitBenefits:
                        l_Payload = new KitBenefitPayload();
                        l_AssignArray.controls.map((gaa) => {
                            if (gaa.get(this.formSelectCheckbox).value) {
                                l_Payload.uniqueIDs.push(gaa.get(this.formUniqueID).value);
                            }
                        });
                        break;

                    case BenefitAddWizardPanel.futureBenefits:
                        l_AssignArray.controls.map((gaa) => {
                            if (gaa.get(this.formSelectCheckbox).value) {
                                l_Payload.uniqueIDs.push(gaa.get(this.formUniqueID).value);
                                l_Payload.selectedBenefitName = gaa.get(this.formEnrollmentName).value;
                            }
                        });
                        break;

                    default:
                        l_AssignArray.controls.map((gaa) => {
                            if (gaa.get(this.formSelectCheckbox).value) {
                                l_Payload.uniqueIDs.push(gaa.get(this.formUniqueID).value);
                                l_Payload.selectedBenefitName = gaa.get(this.formEnrollmentName).value;
                                l_Payload.selectedBenefitAvailable = gaa.get(this.formAvailableOn).value;
                            }
                        });
                        break;
                }
                break;
        }

        this.emitComplete.emit(l_Payload);
    }

    public cancel(): void {
        this.emitCancel.emit();
    }

    public selectedBuyUpOptionalBenefit(event: KitBenefit) {
        if (this.enrollmentManagerService.benefitWizardControlPanel.wizardType == BenefitWizardType.BuyUp &&
            this.enrollmentManagerService.benefitWizardControlPanel.panel == BenefitBuyupWizardPanel.optionalBenefits) {
            const l_FormArray = <FormArray>this.benefitSelectionForm.get(this.benefitSelectionFormArrayName) as FormArray;
            l_FormArray.controls.map((fa) => {
                if (fa.get(this.formUniqueID).value == event.uniqueID) {
                    let l_IsOneSlideToggleSelected = fa.get(this.formIsOneSlideToggleSelected);
                    if (fa.get('isAddSelected').value || fa.get('isChangeSelected').value || fa.get('isTermSelected').value) {
                        l_IsOneSlideToggleSelected.setValue(true);
                    } else {
                        l_IsOneSlideToggleSelected.setValue(null);
                    }
                    l_IsOneSlideToggleSelected.updateValueAndValidity();
                }
            });
        }
    }
}

